// Lib
import React from 'react';
import { useSelector } from 'react-redux';

// Utils
import { length } from '../../../../../../common/utils/immutableHelper';
import { getColorDisplayValue } from '../../../../../../common/colors/elementColorFormatUtil';
import { getThemeIsDarkMode } from '../../../../../user/account/accountModal/interface/themeSettings/themeSelector';
import { getBackgroundColorHex } from '../../../../../element/table/utils/tableCellFormattingUtils';
import { setHotTableCellBackgroundColor } from '../../../../../element/table/utils/hotInstanceUtils/hotTableCellMetaUpdateUtils';
import {
    selectActiveTableCellSelection,
    selectActiveTableCurrentCellBackgroundColor,
} from '../../../../../element/table/store/selectors/activeHotTableSelectors';
import { updateActiveHotTableCellBackgroundColor } from '../../../../../element/table/utils/hotInstanceUtils/activeHotTableMutationUtils';

// Components
import ToolbarPopupTool from '../../ToolbarPopupTool';
import DesktopColorPopup from '../colorTools/popup/desktop/DesktopColorPopup';
import ColorToolIcon from '../colorTools/ColorToolIcon';
import { useActiveTableCallback, useActiveTableSelector } from '../../../../../element/table/store/hotTableStoreHooks';
import createSwitchableTableComponent from './createSwitchableTableComponent';
import LegacyTableCellColorTool from './legacy/LegacyTableCellColorTool';

// Constants
import { PopupIds, PopupSections } from '../../../../../components/popupPanel/popupConstants';
import {
    TABLE_DEFAULT_COLORS,
    TABLE_DEFAULT_HEADER_BACKGROUND_COLOR,
} from '../../../../../../common/table/tableConstants';
import { CellSelections } from '../../../../../../common/table/TableTypes';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

// Styles
import './TableCellColorTool.scss';

type ColorToolPopupIconProps = {
    selectedColor: string | null | undefined;
    isPopupOpen: boolean;
    currentCellSelections: CellSelections;
};

const ColorToolPopupIcon: React.FC<ColorToolPopupIconProps> = ({
    selectedColor,
    isPopupOpen,
    currentCellSelections,
}) => {
    const color =
        currentCellSelections?.length > 0
            ? getColorDisplayValue(selectedColor)
            : 'var(--ui-toolbar-tool-disabled-foreground)';
    const selectedColorDisplayValue =
        selectedColor === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR ? 'var(--ws-table-header-row-background)' : color;

    return <ColorToolIcon color={selectedColorDisplayValue} isActive={isPopupOpen} />;
};

type TableCellColorToolProps = {
    element?: ImMNElement;
};

const TableCellColorTool: React.FC<TableCellColorToolProps> = (props) => {
    const isDarkMode = useSelector(getThemeIsDarkMode);
    const currentCellSelections = useActiveTableSelector(selectActiveTableCellSelection);

    const selectedColor = useActiveTableSelector(selectActiveTableCurrentCellBackgroundColor);

    const updateCellBackgroundColor = useActiveTableCallback((activeTable, backgroundColor: string | null) => {
        updateActiveHotTableCellBackgroundColor({
            hotInstance: activeTable.instance,
            session: activeTable.session,
            backgroundColor,
        });
    }, []);

    const updateTemporaryCellColor = useActiveTableCallback(
        (activeTable, tempColor: string | null) =>
            setHotTableCellBackgroundColor(activeTable.instance, undefined, tempColor),
        [],
    );

    return (
        <ToolbarPopupTool
            {...props}
            name="Cell color"
            buttonSelector=".ColorTool .ColorToolIcon"
            popupId={PopupIds.COLOR}
            closeSection={PopupSections.TOOLBAR}
            toolName="color"
            disabled={length(currentCellSelections) === 0}
            // @ts-ignore Struggling with the JSX type inference
            className="table-cell-color-tool"
            customColorHandler={updateCellBackgroundColor}
            IconComponent={ColorToolPopupIcon}
            PopupComponent={DesktopColorPopup}
            selectedColor={selectedColor}
            selectedColorHex={getBackgroundColorHex(selectedColor, isDarkMode)}
            customDefaultColors={Object.values(TABLE_DEFAULT_COLORS)}
            customTempColorHandler={updateTemporaryCellColor}
            currentCellSelections={currentCellSelections}
            preventFocus
        />
    );
};

export default createSwitchableTableComponent<TableCellColorToolProps>(LegacyTableCellColorTool, TableCellColorTool);
